import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { openInvoice } from '@telegram-apps/sdk-react';
import { Text, FixedLayout, Button, Spinner, Placeholder, Modal, Title } from '@telegram-apps/telegram-ui';

import { createSubscriptionPayment } from '@/api/api';
import { Page } from '@/components/Page';
import { useCoursesStore } from '@/stores/coursesStore';
import { renderContent } from '@/helpers/contentRenderer';
import { useAppStore } from '@/stores/appStore';
import styles from './CourseLandingPage.module.css';

const TgStarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 375 359" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'text-top' }}>
    <path d="M370.028 150.206C378.81 142.684 374.136 128.299 362.61 127.375L252.699 118.563C247.839 118.173 243.607 115.098 241.735 110.597L199.39 8.78848C194.949 -1.88828 179.824 -1.88855 175.383 8.78813L133.039 110.597C131.167 115.098 126.933 118.174 122.074 118.564L12.1626 127.375C0.636323 128.299 -4.03736 142.684 4.74451 150.206C4.74451 150.206 4.74451 150.206 35.4049 176.47C45.188 184.85 51.8495 190.556 56.3855 194.442C62.5771 199.745 70.6255 202.294 78.7406 201.514C86.8707 200.731 100.215 199.447 122.119 197.339C178.174 191.945 178.174 191.945 178.174 191.945C186.695 191.125 190.376 202.456 183.001 206.801C183.001 206.801 183.001 206.801 134.342 235.466C115.3 246.684 103.71 253.512 96.6558 257.667C89.6313 261.806 84.6176 268.602 82.7259 276.532C81.3509 282.296 79.3368 290.739 76.3865 303.107C67.089 342.083 67.089 342.083 67.089 342.083C64.406 353.331 76.6422 362.221 86.5104 356.194L180.61 298.719C184.77 296.178 190.003 296.177 194.163 298.718L288.262 356.194C298.13 362.221 310.367 353.331 307.684 342.084L282.1 234.828C280.969 230.086 282.585 225.111 286.288 221.939L370.028 150.206Z" />
  </svg>
);

const currencyLabels: Record<string, React.ReactNode> = {
  USD: '$',
  RUB: '₽',
  XTR: <TgStarIcon />,
};

export function CourseLandingPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { getCourse, ensureCourseLanding, isCourseLandingLoading, error } = useCoursesStore();
  const { entitlements, addEntitlement } = useAppStore();
  const [loadingCurrency, setLoadingCurrency] = useState<string | null>(null);

  useEffect(() => {
    if (courseId) {
      void ensureCourseLanding(courseId);
    }
  }, [courseId, ensureCourseLanding]);

  const course = courseId ? getCourse(courseId) : undefined;
  const isCourseLoading = !course || isCourseLandingLoading(courseId!);

  if (isCourseLoading) {
    return (
      <Page back={true}>
        <Placeholder description={`Курс ${course && ("\"" + course.title + "\"") || ''} загружается...`} className={styles.fixedPlaceholder}>
          <Spinner size="l" />
        </Placeholder>
      </Page>
    );
  }

  if (error || !course) {
    return (
      <Page back={true}>
        <Placeholder description={error ?? 'Курс не найден'} className={styles.fixedPlaceholder}>
          <Text>Ошибка</Text>
        </Placeholder>
      </Page>
    );
  }

  const initiatePayment = async (currency: string) => {
    try {
      setLoadingCurrency(currency);
      const tgInvoiceLink = await createSubscriptionPayment(course.id, currency);
      const invoiceStatus = await openInvoice(tgInvoiceLink, 'url');
      if (invoiceStatus !== 'paid') {
        console.warn("Invoice was not paid", invoiceStatus);
        // TODO: show error message
        return;
      }
      addEntitlement(course.id);
      // TODO close modal
      void navigate(`/course/${courseId}/contents`);
    } catch (error) {
      console.error("Failed to open invoice", error);
    } finally {
      setLoadingCurrency(null);
    }
  };

  const hasEntitlement = entitlements.includes(courseId!);

  return (
    <Page back={true} onBack={() => void navigate('/courses')}>
      <div className={styles.container}>
        {course.landing_page && course.landing_page.content && Array.isArray(course.landing_page.content)
          ? course.landing_page.content
              .filter(Boolean)
              .map((item, i) => (
                <div key={i}>
                  {renderContent(item)}
                </div>
              ))
          : <Text>No content available for this course.</Text>
        }
      </div>

      {hasEntitlement && (
        <FixedLayout vertical={'bottom'} className={styles.fixedLayoutBottom}>
          <Button
            size="l"
            stretched={true}
            mode={'bezeled'}
            onClick={() => void navigate(`/course/${courseId}/contents`)}
          >
            Перейти к курсу
          </Button>
        </FixedLayout>
      )}

      {!hasEntitlement && course.subscription && (
        <Modal
          trigger={
            <FixedLayout vertical={'bottom'} className={styles.fixedLayoutBottom}>
              <Button size="l" stretched={true}>
                Открыть полный доступ
              </Button>
            </FixedLayout>
          }
          modal={true}
          dismissible={true}
          header={<Modal.Header />}
        >
          <div className={styles.modalContent}>
            <Text Component={'p'} className={styles.modalSubtitle}>
              Подписка на курс
            </Text>

            <Title className={styles.modalTitle}>"{course.title}"</Title>

            <Text Component={'p'} className={styles.modalDescription}>
              Получите неограниченный доступ ко всем материалам курса на 3 месяца.
            </Text>

            {Object.entries(course.subscription.pricing).map(([currency, amount], index) => (
              <div key={index} className={styles.pricingButtonContainer}>
                <Button
                  size="l"
                  stretched={true}
                  onClick={() => void initiatePayment(currency)}
                  disabled={loadingCurrency === currency}
                  loading={loadingCurrency === currency}
                >
                  {currencyLabels[currency]} {amount}
                </Button>
              </div>
            ))}

            <Text Component={'p'} className={styles.modalFooterText}>
              Не продлевается автоматически.
            </Text>

            <Text Component={'p'} className={styles.modalFooterText}>
              Бесплатная отмена в течение первой недели.
            </Text>
          </div>
        </Modal>
      )}
    </Page>
  );
}
